import moment from "moment";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Label,
  Pagination,
  Spinner,
  Table,
} from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery } from "react-query";
import { ADMIN } from "../../config/apiConfig";
import { formatToReal } from "../../utils/utils";
import { IoMdSearch } from "react-icons/io";
import EmptyTable from "./EmptyTable";
import { MdOutlineAttachMoney } from "react-icons/md";
import { BsFillBarChartFill } from "react-icons/bs";
import { IoCloudDownload } from "react-icons/io5";
import TableLoading from "../TableLoading";
import { useApplication } from "../../contexts/ApplicationContext";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import AutoCompleteInput from "../inputs/Autocomplete";
import { LuFilter, LuFilterX } from "react-icons/lu";

const AdminReportSurveysTable = ({ isActive = true }) => {
  const { user } = useApplication();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalPriceSelledSum, setTotalPriceSelledSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const onPageChange = (page) => setCurrentPage(page);
  const [searchName, setSearchName] = useState("");
  const [nameParam, setNameParam] = useState("");
  const [selectedQueries, setSelectedQueries] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState({
    label: "Todos os clientes",
    value: 0,
  });

  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [startDate, setStartDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [showFilters, setShowFilters] = useState(true);
  const [renderFilters, setRenderFilters] = useState(false);

  useEffect(() => {
    if (showFilters) {
      setRenderFilters(true); // mostra o conteúdo imediatamente
    } else {
      // espera a animação terminar antes de esconder
      const timeout = setTimeout(() => setRenderFilters(false), 500); // mesmo tempo da transition
      return () => clearTimeout(timeout);
    }
  }, [showFilters]);

  const {
    isLoading: isLoadingCustomers,
    error: errorListCustomers,
    data: listCustomers,
  } = useQuery(
    ["listCustomers"],
    async () => {
      try {
        const { data } = await axios.get(
          `${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1`
        );

        if (!data?.data?.data) {
          throw new Error("Erro ao buscar clientes");
        }

        let customers = data.data.data.map((customer) => ({
          label:
            customer.typeCustomer === "CPF"
              ? customer.name
              : customer.socialReason,
          value: customer.id,
          vcm: customer.vcm,
          typePayment: customer.typePayment,
          credit: customer.credit,
        }));

        customers = [{ label: "Todos os clientes", value: 0 }, ...customers];

        return customers;
      } catch (error) {
        throw new Error(`Erro ao buscar clientes: ${error.message}`);
      }
    },
    { enabled: isActive }
  );

  const {
    isLoading: isLoadingReportSurvey,
    error: errorListReportSurvey,
    data: listReportSurveys,
  } = useQuery(
    [
      "listReportSurveys",
      selectedCustomer,
      startDate,
      endDate,
      currentPage,
      selectedQueries,
      selectedStatuses,
      nameParam,
    ],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_SURVEY_REPORT}/${selectedCustomer?.value}?startDate=${startDate}&endDate=${endDate}&page=${currentPage}&status=${selectedStatuses}&search=${nameParam}`
      );

      setTotalPages(data.data.totalPages);
      setTotalPriceSum(data.data.totalPriceSum);
      setTotalPriceSelledSum(data.data.totalPriceSelledSum);
      setTotalCount(data.data.totalCount);

      return data.data.data;
    },
    { enabled: isActive }
  );

  const handleCheckboxChange = (status, setState) => {
    setState((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status]
    );
  };

  const handleSearchName = (e) => {
    e.preventDefault();
    setNameParam(searchName);
  };

  return (
    <>
      {user?.Company?.hasSurvey ? (
        <div>
          <div className="flex justify-between items-center mb-2">
            <Button
              size="xs"
              onClick={() => setShowFilters((prev) => !prev)}
              className="primary text-white transition-all duration-300"
            >
              <div className="flex items-center gap-2 transition-all duration-300">
                <span>
                  {showFilters ? "Esconder filtros" : "Mostrar filtros"}
                </span>
                <span className={`transition-transform duration-300 `}>
                  {!showFilters ? <LuFilter /> : <LuFilterX />}
                </span>
              </div>
            </Button>
          </div>
          <div className="flex flex-col gap-4">
            {showFilters && (
              <div
                className={`transition-all duration-500 ease-in-out overflow-hidden bg-white rounded-md shadow-sm border border-gray-200 p-4 relative`}
              >
                {renderFilters && (
                  <div className="transition-opacity duration-500 min-w-[200px]">
                    <div className="title-syncx">Filtro avançado</div>
                    <div className="flex flex-col md:flex-row gap-3 md:gap-10">
                      <div className="mt-6 min-w-[250px]">
                        <AutoCompleteInput
                          options={listCustomers}
                          label="Cliente"
                          onChange={(customer) => setSelectedCustomer(customer)}
                          value={selectedCustomer?.value}
                          isLoading={isLoadingCustomers}
                        />
                        {errorListCustomers && (
                          <Alert color="failure" className="mt-2">
                            Ocorreu um erro ao buscar clientes
                          </Alert>
                        )}
                      </div>
                      <div>
                        <div className="font-semibold mt-2 text-sm mb-2">
                          Período
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => {
                              setStartDate(e.target.value);
                              if (
                                moment(e.target.value).isAfter(moment(endDate))
                              ) {
                                setEndDate(e.target.value);
                              }
                            }}
                            className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                          />
                          <div className="text-xs">Até</div>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => {
                              setEndDate(e.target.value);
                            }}
                            min={startDate}
                            className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
                          />
                        </div>
                      </div>
                      <div className="mt-2 text-sm">
                        <div className="font-semibold">Status</div>
                        <ul className="mt-3 flex flex-col md:flex-row gap-2 md:gap-4">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="finalizado"
                              checked={selectedStatuses.includes(1)}
                              onChange={() =>
                                handleCheckboxChange(1, setSelectedStatuses)
                              }
                            />
                            <Label htmlFor="finalizado" className="flex">
                              Finalizado
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="progress"
                              checked={selectedStatuses.includes(0)} // 0 = progress
                              onChange={() =>
                                handleCheckboxChange(0, setSelectedStatuses)
                              }
                            />
                            <Label htmlFor="progress" className="flex">
                              Em progresso
                            </Label>
                          </div>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex-1">
              <div className="title-syncx mt-3 mb-4">Resultado da busca</div>
              {startDate && endDate && (
                <div className="italic text-sm mb-2 flex items-center gap-2">
                  Informações baseadas no período de{" "}
                  <Badge className="w-fit">
                    {moment(startDate).format("DD/MM/yyyy")}
                  </Badge>{" "}
                  até{" "}
                  <Badge className="w-fit">
                    {" "}
                    {moment(endDate).format("DD/MM/yyyy")}
                  </Badge>
                </div>
              )}
              <div className="overflow-x-scroll">
                <div className="flex gap-3 pb-3" style={{ minWidth: "500px" }}>
                  <Card
                    className="w-full bg-gray-700 relative"
                    style={{ minWidth: "150px" }}
                  >
                    <div className="text-sm md:text-md lg:text-lg text-white">
                      Valor Total:
                    </div>
                    <div className="text-2xl text-white font-semibold flex gap-2 items-center">
                      {!isLoadingReportSurvey ? (
                        `${formatToReal(totalPriceSum)}`
                      ) : (
                        <div
                          role="status"
                          className="max-w-sm animate-pulse inline"
                        >
                          <div className="h-7 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                        </div>
                      )}
                    </div>
                    <MdOutlineAttachMoney className="absolute bottom-0 right-0 text-white text-9xl opacity-10" />
                  </Card>
                  <Card
                    className="w-full bg-gray-700 relative"
                    style={{ minWidth: "150px" }}
                  >
                    <div className="text-sm md:text-md lg:text-lg text-white">
                      Valor Total vendido:
                    </div>
                    <div className="text-2xl text-white font-semibold flex gap-2 items-center">
                      {!isLoadingReportSurvey ? (
                        `${formatToReal(totalPriceSelledSum)}`
                      ) : (
                        <div
                          role="status"
                          className="max-w-sm animate-pulse inline"
                        >
                          <div className="h-7 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                        </div>
                      )}
                    </div>
                    <MdOutlineAttachMoney className="absolute bottom-0 right-0 text-white text-9xl opacity-10" />
                  </Card>
                  <Card className="w-full bg-gray-700 relative overflow-hidden">
                    <div className="text-sm md:text-md lg:text-lg text-white">
                      Total de laudos:
                    </div>
                    <div className="text-2xl text-white font-semibold">
                      {!isLoadingReportSurvey ? (
                        `${totalCount}`
                      ) : (
                        <div
                          role="status"
                          className="max-w-sm animate-pulse inline"
                        >
                          <div className="h-7 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                        </div>
                      )}
                    </div>
                    <BsFillBarChartFill className="absolute bottom-0 right-2 text-white text-9xl opacity-10" />
                  </Card>
                </div>
              </div>
              <form
                className="flex gap-1 mt-0 w-full pl-3 pr-2 py-2 md:py-1 rounded-md shadow-sm bg-gray-300"
                onSubmit={handleSearchName}
              >
                <input
                  type="text"
                  placeholder="Busque pelo nome do proprietário no laudo..."
                  className="border-0 py-1 text-sm mt-1 focus:outline-none focus:ring-0 focus:border-gray-300 px-0 w-full bg-gray-300 placeholder:text-gray-700"
                  onChange={(e) => setSearchName(e.target.value)}
                  value={searchName}
                />
                <Button
                  size="sm"
                  className="flex items-center bg-secondary w-20"
                  type="submit"
                >
                  <IoMdSearch />
                </Button>
              </form>

              <small
                className="gap-2 items-center mt-5 mb-1 flex md:hidden justify-center"
                style={{ fontSize: 10 }}
              >
                <span>
                  Deslize para o lado para visualizar todas as informações
                </span>{" "}
                <FaArrowsLeftRight />
              </small>
              <>
                {listReportSurveys?.length > 0 || isLoadingReportSurvey ? (
                  <div className="overflow-x-auto shadow-md rounded-lg mt-0 md:mt-5">
                    <Table striped style={{ fontSize: "13px" }}>
                      <Table.Head>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Cliente
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Data de criação
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Proprietário
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Preço base
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Preço vendido
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Vistoriador
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Status
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Ações
                        </Table.HeadCell>
                      </Table.Head>

                      <Table.Body className="divide-y">
                        {!isLoadingReportSurvey ? (
                          <>
                            {listReportSurveys?.length > 0 && (
                              <>
                                {listReportSurveys?.map((query, index) => (
                                  <Table.Row
                                    key={index}
                                    className="bg-white dark:border-gray-700 dark:bg-gray-800"
                                  >
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                      {query?.Company?.name ||
                                        query?.Company?.socialReason}
                                    </Table.Cell>
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                      {moment(query.createdAt).format(
                                        "DD/MM/yyyy | HH:mm"
                                      )}
                                    </Table.Cell>
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                      {query.ownerName}
                                    </Table.Cell>
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                      {formatToReal(query?.basePrice)}
                                    </Table.Cell>
                                    <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                      {formatToReal(query?.price)}
                                    </Table.Cell>
                                    <Table.Cell className="text-center py-2">
                                      {query.surveyor.name}
                                    </Table.Cell>
                                    <Table.Cell className="text-center py-2">
                                      {query.status ? (
                                        <Badge
                                          color="success"
                                          className="w-fit mx-auto text-xs"
                                        >
                                          Finalizado
                                        </Badge>
                                      ) : (
                                        <Badge
                                          color="warning"
                                          className="w-fit mx-auto text-xs"
                                        >
                                          Em progresso
                                        </Badge>
                                      )}
                                    </Table.Cell>
                                    <Table.Cell className="text-center py-2">
                                      {!!query.status && (
                                        <div className="flex gap-2 justify-center">
                                          <a
                                            className="bg-primary shadow w-8 h-7 flex items-center text-white justify-center rounded-md"
                                            href={`${query.pdfURL}`}
                                            target="_blank"
                                          >
                                            <IoCloudDownload fontSize="16" />
                                          </a>
                                        </div>
                                      )}
                                    </Table.Cell>
                                  </Table.Row>
                                ))}
                              </>
                            )}
                          </>
                        ) : (
                          <TableLoading cols={7} />
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                ) : (
                  <EmptyTable icon="search_off">
                    Nenhum registro encontrado com esses filtros
                  </EmptyTable>
                )}
              </>

              {totalPages > 1 && (
                <div className="flex overflow-x-auto justify-center mt-3">
                  <Pagination
                    layout="pagination"
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                    previousLabel=""
                    nextLabel=""
                    showIcons
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <ForbiddenQueriesPermission />
      )}
    </>
  );
};

export default AdminReportSurveysTable;
